import React, { useEffect, useState, useRef } from "react";
import Header from "../components/Header";
import { db, auth } from "../services/firebase";
import {
  ref,
  query as rdbQuery,
  orderByChild,
  onValue,
} from "firebase/database";
import { onAuthStateChanged } from "firebase/auth";

const styles = `
:root {
  --primary: #084bcc;
  --primary-hover: #063699;
  --primary-light: #2b6ce4;
  --primary-rgb: 8,75,204;
  --table-header-bg: rgba(var(--primary-rgb), 0.06);
  --table-header-solid: #eaf4ff;
  --gray-25: #fcfcfd;
  --gray-50: #f9fafb;
  --gray-100: #f3f4f6;
  --gray-200: #e5e7eb;
  --gray-300: #d1d5db;
  --gray-400: #9ca3af;
  --gray-500: #6b7280;
  --gray-600: #4b5563;
  --gray-700: #374151;
  --gray-900: #111827;
  --border: #e5e7eb;
  --border-focus: #2563eb;
  --shadow-sm: 0 1px 2px rgba(0,0,0,0.05);
  --shadow: 0 4px 12px rgba(0,0,0,0.07);
  --shadow-lg: 0 10px 25px rgba(0,0,0,0.08);
  --radius-sm: 6px;
  --radius: 10px;
  --radius-lg: 12px;
  --transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

* { box-sizing: border-box; }

body {
  margin: 0;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  background: linear-gradient(180deg, #f6f9ff 0%, #eef6ff 100%);
  color: var(--gray-900);
  line-height: 1.6;
}

.page-wrap {
  max-width: 1100px;
  margin: 0 auto;
  padding: 2rem 1.25rem;
}

/* Status Bar */
.status-bar {
  background: var(--gray-25);
  backdrop-filter: blur(10px);
  padding: 0.875rem 1.5rem;
  border-radius: var(--radius-lg);
  border: 1px solid var(--border);
  margin-bottom: 1.5rem;
  font-size: 0.875rem;
  color: var(--gray-600);
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: var(--shadow-sm);
}

.status-left { display: flex; align-items: center; gap: 0.5rem; }
.status-right { display: flex; align-items: center; gap: 0.5rem; }

/* Warning */
.warning {
  background: #fefce8;
  border: 1px solid #f8d862;
  color: #92400e;
  padding: 1rem 1.25rem;
  border-radius: var(--radius);
  margin-bottom: 2rem;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

/* Hero Section */
.hero {
  background: white;
  padding: 2.5rem;
  border-radius: var(--radius-lg);
  border: 1px solid var(--border);
  box-shadow: var(--shadow);
  margin-bottom: 2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.hero-content {
  flex: 1 1 auto;
  min-width: 0;
}

.hero-cta {
  margin-left: auto;
  display: flex;
  align-items: center;
}

.hero-content h1 {
  margin: 0 0 0.5rem 0;
  font-size: 2rem;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: var(--primary);
}

.hero-content p {
  margin: 0;
  color: var(--gray-600);
  font-size: 1rem;
  font-weight: 400;
  max-width: 500px;
}

/* Table Container */
.table-container {
  background: white;
  border-radius: var(--radius-lg);
  border: 1px solid var(--border);
  box-shadow: var(--shadow);
  overflow: hidden;
}

 .table-header {
  padding: 1.75rem 2rem;
  border-bottom: 1px solid var(--border);
  background: transparent; /* keep this section clean — column headers retain color */
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
 }

.table-title {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 800;
  color: var(--primary-hover);
  font-family: 'Poppins', Inter, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  letter-spacing: -0.01em;
}

.table-stats {
  color: var(--gray-500);
  font-size: 0.9rem;
  font-weight: 500;
}

.table-search {
  position: relative;
  flex: 1;
  max-width: 360px;
}

.stat-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(var(--primary-rgb), 0.08);
  color: var(--primary);
  padding: 0.35rem 0.75rem;
  border-radius: 999px;
  font-weight: 600;
  font-size: 0.85rem;
  margin-left: 0.75rem;
  border: 1px solid rgba(var(--primary-rgb), 0.12);
}

.table-search-input {
  width: 100%;
  padding: 0.875rem 3.6rem 0.875rem 2.75rem;
  border: 2px solid var(--gray-200);
  border-radius: var(--radius);
  font-size: 0.9rem;
  background: white;
  transition: var(--transition);
}

.table-search-input:focus {
  outline: none;
  border-color: var(--border-focus);
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

.table-search-icon {
  position: absolute;
  left: 0.875rem;
  top: 50%;
  transform: translateY(-50%);
  width: 18px;
  height: 18px;
  color: var(--gray-400);
}

.table-search-button {
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  width: 34px;
  height: 34px;
  border-radius: 8px;
  border: none;
  background: transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--gray-500);
}

.table-search-button:hover {
  background: rgba(37,99,235,0.06);
  color: var(--primary);
}

.table-search-button svg { width: 18px; height: 18px; }

.btn {
  padding: 0.875rem 1.75rem;
  border: none;
  border-radius: var(--radius);
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: var(--transition);
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
  white-space: nowrap;
}

.btn-primary {
  background: var(--primary);
  color: white;
  box-shadow: 0 6px 18px rgba(31,66,135,0.08);
  border: 1px solid rgba(0,0,0,0.04);
}

.btn-primary:hover {
  background: var(--primary-hover);
  transform: translateY(-1px);
  box-shadow: 0 8px 22px rgba(31,66,135,0.09);
}

.btn-primary:active {
  transform: translateY(0);
  box-shadow: 0 4px 12px rgba(31,66,135,0.06);
}

.btn-sm {
  padding: 0.625rem 1.25rem;
  font-size: 0.85rem;
}

/* Table */
.table-wrapper {
  overflow-x: auto;
}

table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}


/* Table polish */
tbody tr:nth-child(even) { background: #fbfdff; }

tbody tr:hover { background: rgba(11,99,255,0.04); transform: translateY(-1px); box-shadow: 0 6px 18px rgba(11,99,255,0.04); }

.table-wrapper { /* use viewport-based height so header sticks inside this container */
  max-height: calc(100vh - 320px);
  overflow: auto;
  padding-right: 0;
}

.table-wrapper { position: relative; }
.table-wrapper thead th { position: sticky; top: 0; background: var(--table-header-solid); color: var(--primary-hover); z-index: 110; box-shadow: inset 0 -1px 0 rgba(0,0,0,0.03); }


.project-id { display:inline-block; max-width:100%; font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, 'Roboto Mono', 'Courier New', monospace; color: var(--primary-hover); font-weight: 700; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

.td-title { max-width: none; overflow: visible; text-overflow: initial; white-space: normal; word-break: break-word; }

/* Column width allocation: give more space to Title column */
.table-container table th:nth-child(1), .table-container table td:nth-child(1) { width: 22%; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.table-container table th:nth-child(2), .table-container table td:nth-child(2) { width: 48%; }
.table-container table th:nth-child(3), .table-container table td:nth-child(3) { width: 10%; }
.table-container table th:nth-child(4), .table-container table td:nth-child(4) { width: 20%; white-space: nowrap; }
.table-container table td:nth-child(4) { padding-right: 0; }
.table-container table th:last-child, .table-container table td:last-child { padding-right: 0; }

.date-muted { color: var(--gray-600); font-size: 0.9rem; }

th {
  padding: 1rem 1.5rem;
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--gray-500);
  background: var(--table-header-solid);
  border-bottom: 2px solid var(--gray-200);
}

td {
  padding: 1.125rem 1.5rem;
  font-size: 0.9rem;
  border-bottom: 1px solid var(--gray-100);
  vertical-align: middle;
}

.table-container table th, .table-container table td {
  text-align: left;
}

tbody tr:hover {
  background: rgba(37, 99, 235, 0.04);
}

/* Empty State */
.empty-state {
  padding: 4rem 2rem;
  text-align: center;
  color: var(--gray-500);
}

.empty-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--gray-700);
  margin: 0 0 0.5rem 0;
}

.empty-subtitle {
  font-size: 0.95rem;
  margin: 0 0 2rem 0;
}

/* Controls */
.table-controls {
  padding: 1.5rem 2rem;
  background: var(--gray-50);
  border-top: 1px solid var(--border);
}

.toggle-btn {
  background: white;
  border: 1px solid var(--gray-300);
  color: var(--gray-600);
  padding: 0.625rem 1.25rem;
  border-radius: var(--radius);
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: var(--transition);
}

.toggle-btn:hover {
  border-color: var(--primary);
  color: var(--primary);
  box-shadow: var(--shadow-sm);
}

/* Code Block */
.code-block {
  background: #011627;
  color: #d6deeb;
  padding: 1.25rem;
  border-radius: var(--radius);
  font-family: 'SF Mono', Monaco, 'Cascadia Code', monospace;
  font-size: 0.8rem;
  margin-top: 1rem;
  max-height: 320px;
  overflow: auto;
  line-height: 1.5;
}

/* Responsive */
@media (max-width: 768px) {
  .page-wrap { padding: 1.5rem 1rem; }
  .hero { padding: 2rem 1.5rem; }
  .hero { flex-direction: column; align-items: stretch; }
  .hero-cta { margin-left: 0; margin-top: 1rem; }
  .table-header { flex-direction: column; align-items: stretch; gap: 1.5rem; padding: 1.5rem; }
  .table-search { max-width: none; order: -1; }
  .hero-content h1 { font-size: 1.5rem; }
  .table-title { font-size: 1.125rem; }
  .table-container table th:nth-child(4), .table-container table td:nth-child(4) { white-space: normal; }
  .table-container table td:nth-child(4) { padding-right: 1rem; }
}
`;

const WelcomePage = ({ createNew, onLogout }) => {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [dbError, setDbError] = useState(null);

  useEffect(() => {
    let unsubAuth = () => {};
    try {
      if (auth) {
        unsubAuth = onAuthStateChanged(auth, (u) => {
          // User authentication state tracked but not stored in component state
        });
      }
    } catch (e) {}

    try {
      const q = rdbQuery(ref(db, "priorArts"), orderByChild("createdAt"));
      const onData = (snap) => {
        setDbError(null);
        const val = snap.val() || {};
        const docs = Object.keys(val)
          .map((k) => {
            const raw = val[k] || {};
            let createdAt = null;
            if (raw.createdAt != null) {
              if (typeof raw.createdAt === "number") {
                createdAt = raw.createdAt;
              } else if (
                typeof raw.createdAt === "string" &&
                !isNaN(Number(raw.createdAt))
              ) {
                createdAt = Number(raw.createdAt);
              }
            }
            return { id: k, ...raw, createdAt };
          })
          .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
        setItems(docs);
      };
      const onErr = (err) => {
        setDbError(err && err.code ? err.code : String(err));
        setItems([]);
      };
      const unsub = onValue(q, onData, onErr);
      return () => {
        try {
          unsub();
          unsubAuth();
        } catch (e) {}
      };
    } catch (err) {
      setDbError(err.message || String(err));
    }
  }, []);

  const formatDate = (ts) => {
    if (!ts) return "-";
    try {
      return new Date(ts).toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (e) {
      return "-";
    }
  };

  const filteredItems = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) => {
      const pid = (it.internalProjectId || it.internalPatentId || "")
        .toString()
        .toLowerCase();
      const title = (it.title || "").toString().toLowerCase();
      const author = (it.createdBy || "").toString().toLowerCase();
      return pid.includes(q) || title.includes(q) || author.includes(q);
    });
  }, [items, search]);

  const inputRef = useRef(null);
  const handleSearchButton = () => {
    if (search) {
      setSearch("");
      inputRef.current && inputRef.current.focus();
    } else {
      inputRef.current && inputRef.current.focus();
    }
  };

  return (
    <>
      <style>{styles}</style>
      <Header active="welcome" onLogout={onLogout} />

      <div className="page-wrap">
        {dbError && (
          <div className="status-bar">
            <div className="status-right">
              <span className="error">DB Error: {dbError}</span>
            </div>
          </div>
        )}

        {!db && (
          <div className="warning">
            ⚠️ Firebase not configured. Add config to{" "}
            <strong>.env.local</strong> and restart.
          </div>
        )}

        <div className="hero">
          <div className="hero-content">
            <h1>Prior Art Dashboard</h1>
            <p>
              Centralized platform to manage, track, and collaborate on all your
              Prior Art projects efficiently.
            </p>
          </div>
          <div className="hero-cta">
            <button className="btn btn-primary" onClick={createNew}>
              <svg
                aria-hidden="true"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 5v14M5 12h14" />
              </svg>
              Create New Project
            </button>
          </div>
        </div>

        <div className="table-container">
          <div className="table-header">
            <div
              style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}
            >
              <h2 className="table-title">All Projects</h2>
              <div className="stat-pill">{items.length}</div>
              {filteredItems.length !== items.length && (
                <div className="table-stats">
                  Showing {filteredItems.length} of {items.length} projects
                </div>
              )}
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
              <div className="table-search">
                <svg
                  className="table-search-icon"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                <input
                  ref={inputRef}
                  className="table-search-input"
                  placeholder="Search Projects"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <button
                  type="button"
                  className="table-search-button"
                  onClick={handleSearchButton}
                  aria-label={search ? "Clear search" : "Focus search"}
                >
                  {search ? (
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M18 6L6 18M6 6l12 12" />
                    </svg>
                  ) : (
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
          </div>

          {filteredItems.length === 0 ? (
            <div className="empty-state">
              <h3 className="empty-title">
                {search ? "No matching projects found" : "No projects yet"}
              </h3>
              <p className="empty-subtitle">
                {search
                  ? "Try adjusting your search terms to find what you're looking for."
                  : "Get started by creating your first Prior Art project."}
              </p>
              {!search && (
                <button className="btn btn-primary" onClick={createNew}>
                  <svg
                    aria-hidden="true"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M12 5v14M5 12h14" />
                  </svg>
                  Create First Project
                </button>
              )}
            </div>
          ) : (
            <>
              <div className="table-wrapper">
                <table>
                  <thead>
                    <tr>
                      <th>Project ID</th>
                      <th>Title</th>
                      <th>Created By</th>
                      <th>Date Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredItems.map((item) => (
                      <tr key={item.id}>
                        <td>
                          <strong className="project-id">
                            {item.internalProjectId ||
                              item.internalPatentId ||
                              item.id}
                          </strong>
                        </td>
                        <td>
                          <div className="td-title">{item.title || "-"}</div>
                        </td>
                        <td>{item.createdBy || "-"}</td>
                        <td>
                          <span className="date-muted">
                            {formatDate(item.createdAt)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default WelcomePage;
